import { useCallback, useEffect, useRef, useState } from 'react'
import AssignmentIcon from '@mui/icons-material/Assignment'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import useMosInfo from '../common/provider/useMosInfo'
import MosSmartDialog from '../common/MosSmartDialog'
import createDialogTitle from '../../../generic/utilities/dialogUtil'
import { apiGet, apiPost } from '../../../generic/Api_Functions'
import InspectionReportDetail from './InspectionReportDetail'
import getRandomUUID from '../../../generic/utilities/randomuuid/RandomUUID'

// Inspection report dialog for the task reference currently selected in the MOS context.
// Shared entry point: opened from the Inspection widget and from the task selector rows.
const InspectionReportDialog = ({ open, onClose }) => {
  const { t_ } = useSmartTranslation()
  const { fabricationOrder, taskReference, device, jobReference, notifyInspectionReported } = useMosInfo()
  const acceptRef = useRef()
  const documentTempUUIDMapRef = useRef(new Map())
  const [showAlert, setShowAlert] = useState(null)
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const loadRequestIdRef = useRef(0)

  const getDocumentTempUUID = useCallback((featureId) => {
    const map = documentTempUUIDMapRef.current
    if (!map.has(featureId)) {
      map.set(featureId, getRandomUUID())
    }
    return map.get(featureId)
  }, [])

  useEffect(() => {
    if (!open || !taskReference) {
      // Invalidate any in-flight request so its late response is discarded.
      loadRequestIdRef.current++
      setData([])
      return
    }
    // Tag this request so a slower response from a previously selected task
    // cannot overwrite the data of the task that is currently selected.
    const requestId = ++loadRequestIdRef.current
    apiGet('taskReferenceFeature/' + taskReference.id).then((result) => {
      if (requestId !== loadRequestIdRef.current) {
        return
      }
      setData(result)
    })
  }, [open, taskReference])

  const closeDialog = () => {
    documentTempUUIDMapRef.current = new Map()
    setShowAlert(null)
    onClose()
  }

  const accept = async () => {
    if (loading) return
    const featuresToUpdate = acceptRef.current?.validate()
    if (!featuresToUpdate) return
    if (featuresToUpdate.length === 0) {
      closeDialog()
      return
    }

    const dto = {
      taskReferenceId: taskReference.id,
      fabricationOrderId: fabricationOrder.id,
      jobReferenceId: jobReference.id,
      deviceId: device.id,
      featureValues: featuresToUpdate
    }

    setLoading(true)
    await apiPost('createInspectionReportFeatureList', dto).then(() => {
      // The progress is now different on the server: tell the MOS context so every widget showing
      // inspection progress reloads, whichever widget opened this dialog.
      notifyInspectionReported()
      // Documents have been persisted for this inspection; start fresh temp uuids for the next one.
      closeDialog()
    }).catch((error) => {
      setShowAlert({ severity: 'error', message: error.message })
    }).finally(() => {
      setLoading(false)
    })
  }

  return (
    <MosSmartDialog
      title={createDialogTitle({ color: 'primary', title: t_('Inspection details'), icon: AssignmentIcon })}
      setOpen={open}
      closeCallback={closeDialog}
      acceptCallback={accept}
      cancel={false}
      minWidth='calc(100vw - 4rem)'
      close
      disableButtons={loading}
    >
      <InspectionReportDetail
        features={data}
        acceptRef={acceptRef}
        alert={showAlert}
        getDocumentTempUUID={getDocumentTempUUID}
      />
    </MosSmartDialog>
  )
}

export default InspectionReportDialog
